import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { HandHeart, Quote, MessageCircleHeart, Sparkles, Film, Clock, Users, MapPin, ArrowRight, ExternalLink } from "lucide-react";
import { LOGO_URL } from "@/lib/branding";
import { PATHWAYS } from "@/lib/pathways";
import { SISTER_SITES } from "@/lib/sisterSites";
import TopInvitersLeaderboard from "@/components/TopInvitersLeaderboard";
import { ShopFeatured } from "@/pages/Shop";
import Seo from "@/components/Seo";

const HERO_BG = "https://images.unsplash.com/photo-1511632765486-a01980e01a18?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxwZW9wbGUlMjBodWdnaW5nJTIwd2FybSUyMHN1bnNldHxlbnwwfHx8fDE3ODI1MDE0NzB8MA&ixlib=rb-4.1.0&q=85";
const PRAYER_IMG = "https://images.unsplash.com/photo-1662151900393-97f6bc1567ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwZ3JvdXAlMjBwcmF5aW5nJTIwdG9nZXRoZXIlMjB3YXJtJTIwbGlnaHR8ZW58MHx8fHwxNzgyNTAxNDcwfDA&ixlib=rb-4.1.0&q=85";

const features = [
  { id: "groups", icon: HandHeart, title: "Prayer Groups", body: "Gather in small circles—post requests, mark intercessions, and walk together." },
  { id: "feed", icon: MessageCircleHeart, title: "Memory Feed", body: "Share photos, videos, and Spirit‑led moments. React. Comment. Encourage." },
  { id: "testimonies", icon: Quote, title: "Testimonies", body: "A reverent wall of breakthroughs—where every voice has weight and beauty." },
];

export default function Landing() {
  const { user } = useAuth();
  const [parties, setParties] = useState([]);

  useEffect(() => {
    api.get("/cinema/upcoming-parties").then(({ data }) => setParties(data || [])).catch(() => {});
  }, []);

  const partyDestination = (p) => (user ? `/cinema/${p.movie_id}` : "/register");

  const formatPartyTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  return (
    <div className="bg-cream">
      <Seo
        title="Jesus Just Us — Christian community for prayer, testimonies, and worship"
        description="A quiet, beautiful place to share testimonies, lift prayer, watch faith films, listen to worship, and walk with believers. One faith. One family. One mission."
        path="/"
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FDFBF7] via-[#FDFBF7]/70 to-[#FDFBF7]/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 pb-32 lg:pt-24 lg:pb-44 fade-up">
          <div className="mb-8 inline-flex items-center justify-center rounded-2xl bg-jjblue p-3 shadow-xl ring-1 ring-[var(--jj-gold)]/40">
            <img src={LOGO_URL} alt="Jesus Just Us" className="h-24 w-24 sm:h-28 sm:w-28 object-contain" />
          </div>
          <div className="overline text-burgundy mb-6">Faith · Family · Purpose · Kingdom Impact</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-jjblue leading-[1.02] tracking-tight max-w-3xl">
            Welcome to <span className="text-burgundy italic">Jesus Just Us.</span>
          </h1>
          <p className="mt-6 font-serif text-2xl sm:text-3xl text-burgundy/90 italic">
            &ldquo;One Faith. One Family. One Mission.&rdquo;
          </p>
          <p className="mt-8 max-w-xl text-lg text-[var(--jj-text-secondary)] leading-relaxed">
            A quiet, beautiful place for prayer groups, testimonies, gospel music, and the holy
            memories we share. No noise. No pretending. Just His people, gathered.
          </p>
        </div>
      </section>

      {/* 8-pathway grid (also visible to logged-in users as a quick-jump dashboard) */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 -mt-24 lg:-mt-32 relative z-10">
        <div className="jj-card p-8 sm:p-10 shadow-xl">
          <div className="text-center mb-8">
            <div className="overline text-burgundy">Where would you like to start?</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-jjblue mt-2">Eight ways to walk with us</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {PATHWAYS.map((p) => {
              const Icon = p.icon;
              const isJoin = p.key === "join";
              // If user already logged in, "Join Free" turns into "Open Feed"
              const href = isJoin && user ? "/feed" : p.href;
              const label = isJoin && user ? "Open Feed" : p.label;
              return (
                <Link to={href} key={p.key} data-testid={`pathway-${p.key}`}
                  className={`group jj-card !shadow-none hover:!border-[var(--jj-gold)] hover:-translate-y-0.5 transition-all duration-300 p-5 flex flex-col items-start text-left bg-cream-surface relative overflow-hidden ${isJoin ? "ring-2 ring-[var(--jj-burgundy)] !border-[var(--jj-burgundy)]" : ""}`}>
                  <div className="flex items-center gap-2.5 mb-3">
                    <span className="text-2xl leading-none">{p.emoji}</span>
                    <Icon className="h-5 w-5 text-burgundy" strokeWidth={1.5} />
                  </div>
                  <div className="font-serif text-xl text-jjblue leading-tight">{label}</div>
                  <div className="text-xs text-[var(--jj-text-secondary)] mt-2 leading-relaxed">{p.blurb}</div>
                  {!p.available && (
                    <span className="absolute top-3 right-3 text-[10px] uppercase tracking-wider font-semibold text-gold bg-jjblue/95 px-2 py-0.5 rounded-full" data-testid={`pathway-${p.key}-soon`}>
                      Soon
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
          {!user && (
            <div className="mt-8 text-center">
              <p className="text-sm text-[var(--jj-muted)]">
                Already a member? <Link to="/login" className="text-burgundy font-medium hover:underline">Sign in</Link>
              </p>
            </div>
          )}
        </div>
      </section>

      {/* This week in Faith Cinema — public carousel of scheduled watch parties */}
      {parties.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20" data-testid="landing-watch-parties">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <div className="overline text-burgundy flex items-center gap-2">
                <Film className="h-3.5 w-3.5" /> This week in Faith Cinema
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl text-jjblue mt-2">Press play together.</h2>
              <p className="mt-2 text-[var(--jj-text-secondary)] max-w-xl">
                Members are gathering around films, sermons, and worship sets this week. Join one — or host your own.
              </p>
            </div>
            <Link to={user ? "/cinema" : "/register"} className="hidden sm:inline-flex items-center gap-1.5 text-sm text-burgundy hover:underline" data-testid="landing-cinema-link">
              Browse the library <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-7 flex gap-5 overflow-x-auto pb-4 -mx-6 px-6 snap-x snap-mandatory scroll-smooth">
            {parties.map((p) => (
              <Link
                to={partyDestination(p)}
                key={p.id}
                data-testid={`landing-party-${p.id}`}
                className="group snap-start shrink-0 w-72 sm:w-80 jj-card overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
              >
                <div className="relative aspect-[16/9] bg-jjblue overflow-hidden">
                  {p.poster_url ? (
                    <img src={p.poster_url} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Film className="h-10 w-10 text-cream/40" strokeWidth={1.2} /></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-jjblue/85 to-transparent" />
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-gold text-jjblue text-[10px] font-semibold tracking-wide px-2 py-1 rounded-full uppercase">
                    <Film className="h-3 w-3" /> Watch Party
                  </span>
                  <div className="absolute bottom-3 left-3 right-3 text-cream">
                    <div className="text-[11px] flex items-center gap-1.5 opacity-90"><Clock className="h-3 w-3" /> {formatPartyTime(p.starts_at)}</div>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-lg text-jjblue leading-tight line-clamp-2">{p.title.replace(/^Watch Party:\s*/i, "")}</h3>
                  <div className="mt-3 flex items-center justify-between text-xs text-[var(--jj-muted)]">
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{p.location || "Online"}</span>
                    <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{p.rsvp_count}</span>
                  </div>
                  {p.host?.name && (
                    <div className="mt-3 text-[11px] text-[var(--jj-muted)]">Hosted by {p.host.name}</div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline text-burgundy">A sanctuary, not a feed</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-jjblue mt-4 leading-tight">Built for the small things that change us.</h2>
            <p className="mt-6 text-[var(--jj-text-secondary)] leading-relaxed">
              An honest request. A tearful answer. A memory worth keeping.
            </p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.id} className="jj-card p-7 hover:border-gold transition-colors duration-300" data-testid={`feature-${f.id}`}>
                <f.icon className="h-7 w-7 text-burgundy" strokeWidth={1.4} />
                <h3 className="font-serif text-2xl mt-5 text-jjblue">{f.title}</h3>
                <p className="mt-3 text-[var(--jj-text-secondary)] text-[15px] leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Inviters leaderboard — auto-hides until at least one attributed signup exists */}
      <TopInvitersLeaderboard limit={5} className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 pb-8" />

      {/* Banner */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7 rounded-3xl overflow-hidden border border-jj">
              <img src={PRAYER_IMG} alt="Prayer" className="w-full h-[420px] object-cover" />
            </div>
            <div className="md:col-span-5">
              <div className="overline text-burgundy">Real prayer, real people</div>
              <h2 className="font-serif text-4xl text-jjblue mt-3 leading-tight">Bring your honest heart. Leave with a chorus behind you.</h2>
              <p className="mt-5 text-[var(--jj-text-secondary)] leading-relaxed">
                Post a need. Receive intercession. Mark answers with gratitude. This is what church
                between Sundays can feel like.
              </p>
              <Link to={user ? "/groups" : "/register"} className="btn-gold mt-8 inline-flex items-center gap-2" data-testid="cta-groups">
                <Sparkles className="h-4 w-4" strokeWidth={1.5} /> Find your circle
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Membership preview */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24">
        <div className="text-center max-w-2xl mx-auto">
          <div className="overline text-burgundy">Membership</div>
          <h2 className="font-serif text-4xl lg:text-5xl text-jjblue mt-3">Free to belong. Bless to go deeper.</h2>
          <p className="mt-5 text-[var(--jj-text-secondary)]">
            Begin as a Seeker at no cost. Upgrade when you&rsquo;re ready, and your support keeps this community ad‑free and prayerful.
          </p>
          <Link to="/pricing" className="btn-primary mt-8 inline-block" data-testid="cta-pricing">See membership tiers</Link>
        </div>
      </section>

      {/* The Shop — featured products from Shopify */}
      <ShopFeatured limit={4} />

      {/* The Family — sister Kingdom properties */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20" data-testid="landing-family">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="overline text-burgundy">The family</div>
          <h2 className="font-serif text-4xl lg:text-5xl text-jjblue mt-3 leading-tight">More than a community — a Kingdom family.</h2>
          <p className="mt-5 text-[var(--jj-text-secondary)]">
            Sister properties built alongside JJU. Same heart. Same mission. One click away.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          {SISTER_SITES.map((s) => {
            const Icon = s.icon;
            const accentRing = s.accent === "gold" ? "hover:ring-[var(--jj-gold)]/60" : "hover:ring-[var(--jj-burgundy)]/40";
            const accentBg = s.accent === "gold" ? "bg-gold/15 text-burgundy" : "bg-burgundy/10 text-burgundy";
            return (
              <a
                key={s.key}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`jj-card p-7 sm:p-8 transition-all duration-300 hover:-translate-y-1 hover:ring-1 ${accentRing} flex flex-col`}
                data-testid={`landing-family-${s.key}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`h-12 w-12 rounded-2xl ${accentBg} flex items-center justify-center shrink-0`}>
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <div className="overline text-burgundy">{s.badge}</div>
                </div>
                <h3 className="font-serif text-2xl text-jjblue mt-5 leading-tight">{s.name}</h3>
                <p className="mt-2 text-sm text-burgundy/85 italic">{s.tagline}</p>
                <p className="mt-4 text-[var(--jj-text-secondary)] leading-relaxed flex-1">{s.description}</p>
                <span className="mt-6 text-burgundy font-medium text-sm inline-flex items-center gap-1.5">
                  {s.cta} <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.8} />
                </span>
              </a>
            );
          })}
        </div>
        <div className="mt-10 text-center">
          <Link to="/family" className="text-sm text-burgundy hover:underline inline-flex items-center gap-1" data-testid="landing-family-more">
            Meet the whole family <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Final footer is rendered globally in App.js */}
    </div>
  );
}
